import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../config/axios';
import {
  MessageSquare,
  Menu,
  X,
  Loader2,
  Mail,
  Phone,
  MapPin,
  ArrowRight,
  Users,
  Brain,
  Globe,
  Shield,
  Rocket,
  CheckCircle,
  Sparkles,
  Bot,
  MailPlus,
  PieChart,
  MessageCircle,
  Send,
  Target,
  Star,
  ChevronRight,
  Play,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Youtube,
  ChevronUp,
} from 'lucide-react';

const Landing = () => {
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [stats, setStats] = useState({
    messagesSent: 0,
    leadsGenerated: 0,
    activeUsers: 0,
    satisfaction: 0
  });

  const observerRef = useRef(null);
  const statsRef = useRef(null);

  useEffect(() => {
    apiClient.get('/api/plans')
      .then((res) => {
        if (res.data?.success && res.data.plans) setPlans(res.data.plans);
      })
      .catch(() => setPlans([]))
      .finally(() => setPlansLoading(false));

    // Animate stats on scroll
    const animateStats = () => {
      const targetStats = {
        messagesSent: 15000000,
        leadsGenerated: 250000,
        activeUsers: 5000,
        satisfaction: 98
      };

      const duration = 2000;
      const steps = 60;
      const interval = duration / steps;
      let currentStep = 0;

      const timer = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;

        setStats({
          messagesSent: Math.floor(targetStats.messagesSent * progress),
          leadsGenerated: Math.floor(targetStats.leadsGenerated * progress),
          activeUsers: Math.floor(targetStats.activeUsers * progress),
          satisfaction: Math.floor(targetStats.satisfaction * progress)
        });

        if (currentStep >= steps) {
          clearInterval(timer);
          setStats(targetStats);
        }
      }, interval);

      return timer;
    };

    // Set up intersection observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateStats();
            observerRef.current?.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observerRef.current.observe(statsRef.current);
    }

    // Scroll to top button visibility
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observerRef.current?.disconnect();
    };
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const elementPosition = el.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    setContactSending(true);
    // Placeholder - replace with actual contact API when available
    setTimeout(() => {
      setContactSent(true);
      setContactForm({ name: '', email: '', message: '' });
      setContactSending(false);
      setTimeout(() => setContactSent(false), 5000);
    }, 800);
  };

  const navLinks = [
    { label: 'Features', id: 'features' },
    { label: 'How It Works', id: 'how-it-works' },
    { label: 'Pricing', id: 'pricing' },
    { label: 'Testimonials', id: 'testimonials' },
    { label: 'FAQ', id: 'faq' },
    { label: 'Contact', id: 'contact' },
  ];

  const features = [
    {
      icon: <Brain className="w-8 h-8" />,
      title: 'AI-Powered Lead Qualification',
      description: 'Intelligent chatbots that qualify leads 24/7 using natural language processing and sentiment analysis.',
      gradient: 'from-purple-500 to-pink-500',
      stats: '98% accuracy'
    },
    {
      icon: <MessageCircle className="w-8 h-8" />,
      title: 'WhatsApp Business API',
      description: 'Seamless integration with WhatsApp for automated messaging, templates, and rich media support.',
      gradient: 'from-emerald-500 to-teal-500',
      stats: '2s response time'
    },
    {
      icon: <MailPlus className="w-8 h-8" />,
      title: 'Email Automation Sequences',
      description: 'Create sophisticated email campaigns with AI-generated content and smart scheduling.',
      gradient: 'from-blue-500 to-cyan-500',
      stats: '45% open rate'
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Multi-Channel Campaigns',
      description: 'Orchestrate coordinated campaigns across WhatsApp, email, and SMS for maximum reach.',
      gradient: 'from-orange-500 to-red-500',
      stats: '3x engagement'
    },
    {
      icon: <PieChart className="w-8 h-8" />,
      title: 'Predictive Analytics',
      description: 'AI-powered insights to predict lead conversion and optimize your automation workflows.',
      gradient: 'from-indigo-500 to-purple-500',
      stats: '92% accuracy'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'GDPR & Compliance',
      description: 'Enterprise-grade security with end-to-end encryption and compliance with global regulations.',
      gradient: 'from-gray-700 to-gray-900',
      stats: '100% secure'
    }
  ];

  const howItWorks = [
    {
      step: 1,
      title: 'Connect Your Channels',
      description: 'Link your WhatsApp Business account and email provider in minutes with our simple integration.',
      icon: <Globe className="w-12 h-12" />,
      color: 'from-emerald-500 to-teal-500'
    },
    {
      step: 2,
      title: 'Build AI Workflows',
      description: 'Create intelligent automation flows using our drag-and-drop builder with AI suggestions.',
      icon: <Bot className="w-12 h-12" />,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      step: 3,
      title: 'Watch Leads Convert',
      description: 'Sit back as our AI engages, qualifies, and nurtures leads across multiple channels automatically.',
      icon: <Rocket className="w-12 h-12" />,
      color: 'from-purple-500 to-pink-500'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Marketing Director',
      company: 'TechStart Inc.',
      image: 'https://images.unsplash.com/photo-1494790108777-263ef5a1f0c3?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&q=80',
      content: 'Leads Automater transformed our lead generation. We\'re seeing 3x more qualified leads with 80% less manual work.',
      rating: 5,
      stats: { leads: '+300%', time: '-80%' }
    },
    {
      name: 'Michael Chen',
      role: 'Founder',
      company: 'GrowthLabs',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&q=80',
      content: 'The AI-powered WhatsApp automation is incredible. It feels like having a 24/7 sales team that never sleeps.',
      rating: 5,
      stats: { conversion: '+150%', revenue: '+200%' }
    },
    {
      name: 'Priya Patel',
      role: 'Head of Sales',
      company: 'EcoSolutions',
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&q=80',
      content: 'The multi-channel automation is a game-changer. We nurture leads seamlessly across WhatsApp and email.',
      rating: 5,
      stats: { engagement: '+250%', response: '-90%' }
    }
  ];

  const faqs = [
    {
      question: 'How does AI lead qualification work?',
      answer: 'Our AI uses natural language processing to understand lead intent, sentiment, and qualification criteria. It engages in natural conversations, asks qualifying questions, and scores leads based on your custom criteria, all while maintaining a human-like interaction.'
    },
    {
      question: 'Can I integrate with my existing CRM?',
      answer: 'Absolutely! We offer seamless integrations with major CRMs like Salesforce, HubSpot, and Pipedrive. Leads are automatically synced with all their conversation history and qualification data.'
    },
    {
      question: 'What platforms do you support?',
      answer: 'We currently support WhatsApp Business API, email (Gmail, Outlook, custom SMTP), SMS, and Facebook Messenger. We\'re constantly adding new channels based on customer demand.'
    },
    {
      question: 'Is it compliant with data protection regulations?',
      answer: 'Yes, we are fully GDPR and CCPA compliant. All data is encrypted end-to-end, and we provide complete data export and deletion capabilities. We also offer enterprise-grade security features like SSO and audit logs.'
    }
  ];

  const stats_data = [
    { label: 'Messages Sent', value: stats.messagesSent.toLocaleString(), icon: <Send className="w-6 h-6" />, suffix: '+' },
    { label: 'Leads Generated', value: stats.leadsGenerated.toLocaleString(), icon: <Users className="w-6 h-6" />, suffix: '+' },
    { label: 'Active Users', value: stats.activeUsers.toLocaleString(), icon: <Users className="w-6 h-6" />, suffix: '+' },
    { label: 'Satisfaction', value: stats.satisfaction, icon: <Star className="w-6 h-6" />, suffix: '%' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 p-3 bg-emerald-600 text-white rounded-full shadow-lg hover:bg-emerald-700 transition-all transform hover:scale-110 animate-bounce"
          aria-label="Scroll to top"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
      )}

      {/* Sticky Header with Glassmorphism */}
      <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-xl border-b border-gray-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity"></div>
                <div className="relative w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                Leads Automater
              </span>
            </Link>

            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollTo(link.id)}
                  className="relative text-sm font-medium text-gray-600 hover:text-emerald-600 transition-colors group"
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-emerald-500 to-teal-600 group-hover:w-full transition-all duration-300"></span>
                </button>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-4">
              <Link
                to="/login"
                className="text-sm font-medium text-gray-600 hover:text-emerald-600 transition-colors px-4 py-2"
              >
                Login
              </Link>
              <Link
                to="/register"
                className="relative inline-flex items-center px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-sm font-medium rounded-xl hover:from-emerald-600 hover:to-teal-700 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 group"
              >
                Get Started
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white/95 backdrop-blur-xl py-4 px-4">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollTo(link.id)}
                  className="text-left py-3 px-4 text-gray-700 hover:bg-gradient-to-r hover:from-emerald-50 hover:to-teal-50 hover:text-emerald-600 rounded-xl font-medium transition-all"
                >
                  {link.label}
                </button>
              ))}
              <div className="h-px bg-gray-200 my-2"></div>
              <Link
                to="/login"
                className="py-3 px-4 text-gray-700 hover:bg-gray-50 rounded-xl font-medium"
              >
                Login
              </Link>
              <Link
                to="/register"
                className="py-3 px-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-medium text-center hover:from-emerald-600 hover:to-teal-700 transform hover:scale-105 transition-all"
              >
                Get Started
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section with Animated Background */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/50">
        {/* Animated Grid Background */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 animate-float">
          <div className="w-20 h-20 bg-emerald-500/10 rounded-2xl backdrop-blur-xl border border-emerald-500/20 flex items-center justify-center">
            <MessageCircle className="w-10 h-10 text-emerald-600" />
          </div>
        </div>
        <div className="absolute bottom-20 right-10 animate-float-delayed">
          <div className="w-24 h-24 bg-teal-500/10 rounded-3xl backdrop-blur-xl border border-teal-500/20 flex items-center justify-center">
            <Mail className="w-12 h-12 text-teal-600" />
          </div>
        </div>
        <div className="absolute top-40 right-20 animate-float-slow">
          <div className="w-16 h-16 bg-purple-500/10 rounded-xl backdrop-blur-xl border border-purple-500/20 flex items-center justify-center">
            <Bot className="w-8 h-8 text-purple-600" />
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-32">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-sm border border-emerald-200 rounded-full px-4 py-2 mb-8">
              <Sparkles className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-700">AI-Powered Lead Generation</span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 tracking-tight">
              Automate WhatsApp & Email
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600">
                with AI-Powered Intelligence
              </span>
            </h1>

            <p className="mt-6 text-xl sm:text-2xl text-gray-600 max-w-3xl mx-auto">
              Transform your lead generation with intelligent automation. Capture, qualify, and nurture leads across WhatsApp and email 24/7.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="relative inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 group overflow-hidden"
              >
                <span className="relative z-10">Choose Plan</span>
                <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-teal-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </Link>
              <button
                onClick={() => scrollTo('how-it-works')}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-gray-700 font-semibold rounded-2xl border-2 border-gray-200 hover:border-emerald-500 hover:text-emerald-600 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5"
              >
                <Play className="w-5 h-5" />
                See how it works
              </button>
            </div>

            {/* Social Proof */}
            <div className="mt-12 flex items-center justify-center gap-8">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <img
                    key={i}
                    src={`https://images.unsplash.com/photo-${i === 1 ? '1494790108777-263ef5a1f0c3' : i === 2 ? '1507003211169-0a1dd7228f2d' : i === 3 ? '1573496359142-b8d87734a5a2' : '1517841905240-472988babdf9'}?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80`}
                    alt={`User ${i}`}
                    className="w-10 h-10 rounded-full border-2 border-white object-cover"
                  />
                ))}
              </div>
              <div className="text-left">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-sm text-gray-600">Trusted by 5,000+ businesses</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="py-16 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats_data.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-2xl mb-4 group-hover:scale-110 transition-transform">
                  <div className="text-emerald-600">{stat.icon}</div>
                </div>
                <div className="text-3xl font-bold text-gray-900">
                  {stat.value}{stat.suffix}
                </div>
                <div className="text-sm text-gray-600 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900">
              Powerful Features for
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                Modern Lead Generation
              </span>
            </h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to automate, qualify, and convert leads across WhatsApp and email with AI.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group relative bg-white rounded-3xl p-8 border border-gray-200 hover:border-transparent hover:shadow-2xl transition-all duration-500 overflow-hidden"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>

                <div className={`relative w-16 h-16 bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center mb-6 text-white transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                  {feature.icon}
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 mb-4">{feature.description}</p>

                <div className="flex items-center justify-between">
                  <span className={`text-sm font-semibold bg-gradient-to-r ${feature.gradient} bg-clip-text text-transparent`}>
                    {feature.stats}
                  </span>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" />
                </div>

                {/* Decorative Line */}
                <div className={`absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r ${feature.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900">
              Get Started in
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                Three Simple Steps
              </span>
            </h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              From setup to automation in minutes, not days.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {howItWorks.map((item, index) => (
              <div key={index} className="relative group">
                {/* Step Number */}
                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                  <div className={`w-12 h-12 bg-gradient-to-r ${item.color} rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    {item.step}
                  </div>
                </div>

                {/* Connector Line */}
                {index < howItWorks.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 left-full w-full h-0.5 bg-gradient-to-r from-emerald-500/30 to-transparent transform -translate-y-1/2"></div>
                )}

                <div className="pt-12 text-center">
                  <div className={`inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br ${item.color} bg-opacity-10 rounded-3xl mb-6 text-transparent bg-clip-text`}>
                    <div className={`text-${item.color.split(' ')[1]}`}>{item.icon}</div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900">
              Simple, Transparent
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                Pricing Plans
              </span>
            </h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the perfect plan for your business. The Free Trial plan provides a one-time 7-day trial per user.
            </p>
          </div>

          {plansLoading ? (
            <div className="flex justify-center py-12">
              <div className="relative">
                <Loader2 className="w-12 h-12 animate-spin text-emerald-600" />
                <div className="absolute inset-0 blur-xl bg-emerald-500/20 animate-pulse"></div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {plans
                .filter((plan) => {
                  const slug = String(plan.slug || '').toLowerCase();
                  if (slug === 'internal-lifetime') return false;
                  const pm = Number(plan.price_paise_monthly ?? plan.price_paise ?? 0);
                  const py = Number(plan.price_paise_yearly ?? 0);
                  if (slug === 'free') return true;
                  return pm > 0 || py > 0;
                })
                .map((plan, index) => {
                  const pm = Number(plan.price_paise_monthly ?? plan.price_paise ?? 0);
                  const py = Number(plan.price_paise_yearly ?? 0);
                  const slug = String(plan.slug || '').toLowerCase();
                  const isFreeTrialPlan = slug === 'free';
                  const isPopular = String(plan.slug || '').toLowerCase() === 'pro';
                  return (
                <div
                  key={plan.id}
                  className={`group relative rounded-3xl p-8 transition-all duration-500 hover:scale-105 ${(isPopular && !isFreeTrialPlan)
                    ? 'bg-gradient-to-b from-gray-900 to-gray-800 text-white shadow-2xl border-2 border-emerald-500'
                    : 'bg-white border-2 border-gray-200 hover:border-emerald-500 shadow-xl'
                    }`}
                >
                  {isPopular && !isFreeTrialPlan && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-sm font-semibold px-4 py-1 rounded-full">
                        Most Popular
                      </span>
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className={`text-2xl font-bold mb-2 ${(isPopular && !isFreeTrialPlan) ? 'text-white' : 'text-gray-900'}`}>
                      {plan.name}
                    </h3>
                    <div className="flex items-baseline gap-1">
                      <span className={`text-4xl font-bold ${(isPopular && !isFreeTrialPlan) ? 'text-white' : 'text-gray-900'}`}>
                        {isFreeTrialPlan ? 'Free' : `₹${(pm / 100).toFixed(0)}`}
                      </span>
                      {!isFreeTrialPlan && (
                        <span className={`text-sm ${(isPopular && !isFreeTrialPlan) ? 'text-gray-300' : 'text-gray-500'}`}>
                          /month
                        </span>
                      )}
                      {!isFreeTrialPlan && py > 0 && (
                        <span className={`text-xs ${(isPopular && !isFreeTrialPlan) ? 'text-gray-400' : 'text-gray-500'}`}>
                          (₹{(py / 100).toFixed(0)}/year)
                        </span>
                      )}
                    </div>
                    <p className={`text-sm mt-2 ${(isPopular && !isFreeTrialPlan) ? 'text-gray-300' : 'text-gray-500'}`}>
                      {plan.max_workspaces} workspace{plan.max_workspaces > 1 ? 's' : ''}
                    </p>
                    {isFreeTrialPlan && (
                      <p className="text-xs mt-1 text-emerald-700 font-medium">7-day trial (once per user)</p>
                    )}
                  </div>

                  <ul className="space-y-4 mb-8">
                    {[
                      'AI Lead Qualification',
                      'WhatsApp Automation',
                      'Email Sequences',
                      'Analytics Dashboard',
                      (isPopular && !isFreeTrialPlan) ? 'Priority Support' : 'Email Support',
                      (isPopular && !isFreeTrialPlan) ? 'Custom Integrations' : 'Basic Integrations'
                    ].map((feature, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <CheckCircle className={`w-5 h-5 ${(isPopular && !isFreeTrialPlan) ? 'text-emerald-400' : 'text-emerald-600'}`} />
                        <span className={(isPopular && !isFreeTrialPlan) ? 'text-gray-200' : 'text-gray-600'}>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/register"
                    className={`block w-full py-4 text-center font-semibold rounded-2xl transition-all transform hover:-translate-y-0.5 ${(isPopular && !isFreeTrialPlan)
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700 shadow-lg hover:shadow-xl'
                      : isFreeTrialPlan
                        ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                      }`}
                  >
                    Choose Plan
                  </Link>
                </div>
              )})}
            </div>
          )}

          <div className="text-center mt-12">
            <p className="text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-emerald-600 hover:text-emerald-700 font-semibold">
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900">
              Loved by
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                ️5,000+ Businesses
              </span>
            </h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              See what our customers say about transforming their lead generation with AI automation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="group relative bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 border border-gray-200 hover:border-emerald-500 hover:shadow-2xl transition-all duration-500"
              >
                {/* Quote Mark */}
                <div className="absolute top-6 right-6 text-6xl text-emerald-500/20 font-serif">"</div>

                <div className="flex items-center gap-4 mb-6">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-2xl object-cover ring-4 ring-emerald-500/20 group-hover:ring-emerald-500/40 transition-all"
                  />
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                    <p className="text-xs text-emerald-600">{testimonial.company}</p>
                  </div>
                </div>

                <p className="text-gray-700 mb-6 italic">"{testimonial.content}"</p>

                <div className="flex items-center gap-2 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                  {Object.entries(testimonial.stats).map(([key, value]) => (
                    <div key={key} className="text-center">
                      <div className="text-lg font-bold text-emerald-600">{value}</div>
                      <div className="text-xs text-gray-500 capitalize">{key}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Trust Badges */}
          <div className="mt-16 text-center">
            <p className="text-sm text-gray-500 mb-6">Trusted by leading companies worldwide</p>
            <div className="flex flex-wrap justify-center items-center gap-12 opacity-50">
              {['TechStart', 'GrowthLabs', 'EcoSolutions', 'InnovateCo', 'FutureTech'].map((company) => (
                <span key={company} className="text-xl font-bold text-gray-400">{company}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900">
              Frequently Asked
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                Questions
              </span>
            </h2>
            <p className="mt-4 text-xl text-gray-600">
              Got questions? We've got answers.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:border-emerald-500 transition-colors"
              >
                <button
                  onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between group"
                >
                  <span className="font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors">
                    {faq.question}
                  </span>
                  <ChevronRight
                    className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${activeFaq === index ? 'rotate-90' : ''
                      }`}
                  />
                </button>
                <div
                  className={`px-6 overflow-hidden transition-all duration-300 ${activeFaq === index ? 'pb-4 max-h-40' : 'max-h-0'
                    }`}
                >
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600">
              Still have questions?{' '}
              <button
                onClick={() => scrollTo('contact')}
                className="text-emerald-600 hover:text-emerald-700 font-semibold"
              >
                Contact our support team
              </button>
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-emerald-600 to-teal-600 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-30"></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Lead Generation?
          </h2>
          <p className="text-xl text-white/90 mb-10">
            Join 5,000+ businesses already automating their WhatsApp and email lead generation with AI.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-white text-emerald-600 font-semibold rounded-2xl hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 group"
            >
              Start Your Free Trial
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <button
              onClick={() => scrollTo('contact')}
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-2xl hover:bg-white/10 transition-all transform hover:-translate-y-0.5"
            >
              Talk to Sales
            </button>
          </div>
          <p className="text-white/80 text-sm mt-6">
            Free Trial is one-time per user. Paid plans start directly on their selected billing period.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                Get in
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                  Touch
                </span>
              </h2>
              <p className="text-xl text-gray-600 mb-12">
                Have questions about our AI automation platform? We're here to help you transform your lead generation.
              </p>

              <div className="space-y-8">
                <div className="flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:bg-emerald-600 transition-all">
                    <Mail className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Email Us</h4>
                    <p className="text-gray-600">support@leadsautomater.com</p>
                    <p className="text-sm text-gray-500">24/7 Support</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:bg-teal-600 transition-all">
                    <Phone className="w-5 h-5 text-teal-600 group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Call Us</h4>
                    <p className="text-gray-600">+91 98765 43210</p>
                    <p className="text-sm text-gray-500">Mon-Fri 9am to 6pm IST</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:bg-cyan-600 transition-all">
                    <MapPin className="w-5 h-5 text-cyan-600 group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Visit Us</h4>
                    <p className="text-gray-600">Bangalore, India</p>
                    <p className="text-sm text-gray-500">Global remote team</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 border border-gray-200 shadow-xl">
              {contactSent ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                  <p className="text-gray-600">We'll get back to you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                    <input
                      type="text"
                      value={contactForm.name}
                      onChange={(e) => setContactForm((p) => ({ ...p, name: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                    <input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm((p) => ({ ...p, email: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                    <textarea
                      value={contactForm.message}
                      onChange={(e) => setContactForm((p) => ({ ...p, message: e.target.value }))}
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                      placeholder="Tell us about your project..."
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={contactSending}
                    className="relative w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:-translate-y-0.5 shadow-lg group overflow-hidden"
                  >
                    <span className="relative z-10">
                      {contactSending ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Sending...
                        </span>
                      ) : (
                        'Send Message'
                      )}
                    </span>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
            {/* Brand */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold text-white">Leads Automater</span>
              </div>
              <p className="text-gray-400 mb-6">
                Transform your lead generation with AI-powered WhatsApp and email automation.
              </p>
              <div className="flex gap-4">
                {[Facebook, Twitter, Linkedin, Instagram, Youtube].map((Icon, index) => (
                  <button
                    key={index}
                    type="button"
                    className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-emerald-600 transition-colors"
                    aria-label="Social link"
                  >
                    <Icon className="w-5 h-5" />
                  </button>
                ))}
              </div>
            </div>

            {/* Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2">
                {['Features', 'Pricing', 'Integrations', 'API'].map((item) => (
                  <li key={item}>
                    <button
                      onClick={() => scrollTo(item.toLowerCase())}
                      className="text-gray-400 hover:text-emerald-400 transition-colors"
                    >
                      {item}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2">
                {['About', 'Blog', 'Careers', 'Press'].map((item) => (
                  <li key={item}>
                    <span className="text-gray-400">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                {['Help Center', 'Documentation', 'Status', 'Contact'].map((item) => (
                  <li key={item}>
                    <button
                      onClick={() => item === 'Contact' ? scrollTo('contact') : null}
                      className="text-gray-400 hover:text-emerald-400 transition-colors"
                    >
                      {item}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-gray-800 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} Leads Automater. All rights reserved.
            </p>
            <div className="flex gap-6">
              <span className="text-sm text-gray-500">Privacy Policy</span>
              <span className="text-sm text-gray-500">Terms of Service</span>
              <span className="text-sm text-gray-500">Cookie Policy</span>
            </div>
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
        
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-float-delayed {
          animation: float-delayed 7s ease-in-out infinite;
        }
        
        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Landing;